/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import classnames from 'classnames';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {useAnimatedMount} from '@ion/dls/src/hooks/useAnimatedMount';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {showEmergencyDialog} from '@/store/actions/dashboard';
import {settingsSelector} from '@/store/selectors/dashboard-selectors';
import Images from '../img/ImagePaths';
import BaseDialog from './BaseDialog';

export enum ContactTags {
  theftAssist = 'theft-support',
  customerSupport = 'customer-support',
  emergencyContact = 'emergency-contact'
};

const THEFT_ASSIST_CONTACT = 1;
const NO_CONTACT           = 2;

const useStyles = makeStyles((theme: DLSTheme) => ({
  content : {
    display                        : 'flex',
    flexDirection                  : 'column',
    justifyContent                 : 'space-between',
    padding                        : '48px 100px 0px 100px',
    height                         : 660,
    [theme.breakpoints.down('sm')] : {
      padding : '16px 0px',
      width   : '100%'
    }
  },
  details : {
    padding         : '40px 60px',
    backgroundColor : dlsColor('white')
  },
  preface : {
  },
  prefaceHeaderContent : {
  },
  prefaceHeaderText : {
    fontWeight                     : 500,
    fontStyle                      : 'normal',
    fontSize                       : '24px',
    lineHeight                     : '24px',
    color                          : '#262626',
    [theme.breakpoints.down('sm')] : {
      fontSize   : '22px',
      lineHeight : '22px'
    }
  },
  prefaceTextContent : {
    marginTop : 20
  },
  prefaceText : {
    fontWeight : 400,
    fontStyle  : 'normal',
    fontSize   : '14px',
    lineHeight : '16px',
    color      : '#4D4D4D'
  },
  contact : {
    display : 'flex'
  },
  phoneIcon : {
    margin : '7px 0 0 7px'
  },
  step : {
    marginTop                      : 40,
    [theme.breakpoints.down('sm')] : {
      marginTop : 25
    }
  },
  numberText : {
    margin     : '5px 0 0 12px',
    fontWeight : 500
  },
  stepHeaderText : {
    fontWeight : 400,
    fontStyle  : 'normal',
    fontSize   : '17px',
    lineHeight : '16px',
    color      : '#262626'
  },
  stepTextContent : {
    marginTop : 16
  },
  stepText : {
    fontWeight : 400,
    fontStyle  : 'normal',
    fontSize   : '14px',
    lineHeight : '22px',
    color      : '#4D4D4D'
  },
  phone : {
    height          : '32px',
    width           : '32px',
    borderRadius    : '50%',
    backgroundColor : dlsColor('grey6')
  }
}));

export const getContacts = (settings): GenericObject => {
  const numbers: GenericObject = {};
  const contacts = settings?.contactDetails;

  if (contacts?.length > 0) {
    contacts[0].contacts?.forEach(({tag, phone}) => {
      numbers[tag] = phone;
    });
  }

  return numbers;
};

export const getMails = (settings): GenericObject => {
  const mails: GenericObject = {};
  const contacts = settings?.contactDetails;

  if (contacts?.length > 0) {
    contacts[0].contacts?.forEach(({tag, email}) => {
      mails[tag] = email;
    });
  }

  return mails;
};

/**
 * This component provides a dialog to display emergency assistance information
 * to the current user.
 *
 * ```typescript
 *
 * import {EmergencyDialog} from '@ion-bmw-portal/src/components/dialog';
 *
 * <EmergencyDialog />
 *
 * ```
 *
 */
export const EmergencyDialog = () => {
  const dispatch              = useDispatch();
  const {isVisible, onRemove} = useAnimatedMount();
  const classes               = useStyles();
  const settings              = useSelector(settingsSelector);
  const contactDetails        = getContacts(settings);

  const title = translate('common:dialogs.emergency.title');
  const steps = [
    translate('common:dialogs.emergency.steps.1'),
    translate('common:dialogs.emergency.steps.2'),
    translate('common:dialogs.emergency.steps.3')
  ];

  function onClose() {
    onRemove(function() {
      dispatch(showEmergencyDialog(false));
    });
  };

  return (
    <BaseDialog show={isVisible} title={title} onClose={onClose}>
      <div className={classes.content}>
        <div className={classes.details}>
          <div className={classes.preface}>
            <div className={classes.prefaceHeaderContent}>
              <span className={classes.prefaceHeaderText}>
                {translate('common:dialogs.emergency.preface.header')}
              </span>
            </div>
            <div className={classes.prefaceTextContent}>
              <span className={classes.prefaceText}>
                {translate('common:dialogs.emergency.preface.text')}
              </span>
            </div>
          </div>
          <div>
            {
              steps.map((step, i) => {
                const number = contactDetails[
                  i !== THEFT_ASSIST_CONTACT ? ContactTags.emergencyContact : ContactTags.theftAssist
                ];
                return (
                  <div key={`step-${step}`} className={classes.step}>
                    <div>
                      <span className={classes.stepHeaderText}>
                        {`${translate('common:dialogs.emergency.steps.header')} ${i + 1}`}
                      </span>
                    </div>
                    <div className={classes.stepTextContent}>
                      <span className={classes.stepText}>
                        {step}
                      </span>
                    </div>
                    {(i !== NO_CONTACT) && (
                      <div className={classnames(classes.prefaceTextContent, classes.contact)}>
                        <div className={classes.phone}>
                          <img src={Images.Phone} className={classes.phoneIcon}/>
                        </div>
                        <span className={classes.numberText}>
                          {translate('common:dialogs.emergency.call', {number})}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })
            }
          </div>
        </div>
      </div>
    </BaseDialog>
  );
};

export default EmergencyDialog;
